const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

class ApiService {
  constructor() {
    this._onUnauthorized = null;
  }

  setUnauthorizedHandler(handler) {
    this._onUnauthorized = handler;
  }

  async request(endpoint, { authAttempt, ...options } = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    // Gateway errors (Vercel/Railway 502s during deploys) return HTML, not
    // JSON — fall back to an empty body so the user sees "Request failed"
    // instead of a raw JSON SyntaxError.
    let data;
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    // A 401 from signin/signup means bad credentials, not a dead session —
    // those calls set authAttempt so the server's message reaches the user.
    if (response.status === 401 && !authAttempt && this._onUnauthorized) {
      this._onUnauthorized();
      throw new Error('Session expired. Please sign in again.');
    }

    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
  }

  // Auth endpoints
  async signUp(email, password, name, acceptedTerms) {
    return this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, acceptedTerms }),
      authAttempt: true,
    });
  }

  async acceptTerms() {
    return this.request('/auth/accept-terms', { method: 'POST' });
  }

  async signIn(email, password) {
    return this.request('/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      authAttempt: true,
    });
  }

  async signOut() {
    await this.request('/auth/signout', { method: 'POST' });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token, password) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // User endpoints
  async getNearbyUsers(latitude, longitude, radius = 25, handicapMin, handicapMax) {
    const params = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      radius: radius.toString(),
    });
    if (handicapMin !== undefined) params.append('handicapMin', handicapMin);
    if (handicapMax !== undefined) params.append('handicapMax', handicapMax);

    return this.request(`/users/nearby?${params}`);
  }

  async searchUsers(query) {
    return this.request(`/users/search?q=${encodeURIComponent(query)}`);
  }

  async getSuggestedUsers() {
    return this.request('/users/suggested');
  }

  async searchCourses(query = '') {
    const params = query ? `?q=${encodeURIComponent(query)}` : '';
    return this.request(`/users/courses/search${params}`);
  }

  async getMyCourses() {
    return this.request('/users/me/courses');
  }

  async addMyCourse(data) {
    return this.request('/users/me/courses', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateMyCourse(courseId, data) {
    return this.request(`/users/me/courses/${courseId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteMyCourse(courseId) {
    return this.request(`/users/me/courses/${courseId}`, { method: 'DELETE' });
  }

  // Course directory (OSM-seeded + user-contributed) endpoints
  async typeaheadCourses(q, limit) {
    const params = new URLSearchParams({ q });
    if (limit) params.append('limit', limit.toString());
    return this.request(`/courses/typeahead?${params}`);
  }

  async addCourse(data) {
    return this.request('/courses', { method: 'POST', body: JSON.stringify(data) });
  }

  async selectCourse(id) {
    return this.request(`/courses/${id}/select`, { method: 'POST' });
  }

  async getUser(userId) {
    return this.request(`/users/${userId}`);
  }

  async updateProfile(updates) {
    return this.request('/users/me', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async getPublicUser(userId) {
    return this.request(`/users/${userId}/public`);
  }

  // Connection endpoints
  async getConnections() {
    return this.request('/connections');
  }

  async getPendingConnections() {
    return this.request('/connections/pending');
  }

  async getSentConnections() {
    return this.request('/connections/sent');
  }

  async sendConnectionRequest(userId, metAtCourse, note) {
    return this.request('/connections', {
      method: 'POST',
      body: JSON.stringify({ userId, metAtCourse, note }),
    });
  }

  // Signed key that goes in your QR code — a scan with it connects instantly
  async getQrKey() {
    return this.request('/connections/qr-key');
  }

  async connectViaQr(userId, key) {
    return this.request('/connections/qr', {
      method: 'POST',
      body: JSON.stringify({ userId, key }),
    });
  }

  async saveConnectionMemory(connectionId, { metAtCourse, note }) {
    return this.request(`/connections/${connectionId}/memory`, {
      method: 'PUT',
      body: JSON.stringify({ metAtCourse, note }),
    });
  }

  async acceptConnection(connectionId) {
    return this.request(`/connections/${connectionId}/accept`, { method: 'PUT' });
  }

  async declineConnection(connectionId) {
    return this.request(`/connections/${connectionId}/decline`, { method: 'PUT' });
  }

  async removeConnection(connectionId) {
    return this.request(`/connections/${connectionId}`, { method: 'DELETE' });
  }

  async updateConnectionLevel(connectionId, connectionLevel) {
    return this.request(`/connections/${connectionId}/level`, {
      method: 'PUT',
      body: JSON.stringify({ connectionLevel }),
    });
  }

  async getConnectionStatus(userId) {
    return this.request(`/connections/status/${userId}`);
  }

  async getConnectionStatusBatch(userIds) {
    return this.request('/connections/status/batch', {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    });
  }

  // Message endpoints
  async getConversations() {
    return this.request('/messages/conversations');
  }

  async getOrCreateDirectConversation(userId) {
    return this.request('/messages/conversations/direct', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async getMessages(conversationId, limit = 50, before) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (before) params.append('before', before);

    return this.request(`/messages/conversations/${conversationId}/messages?${params}`);
  }

  async sendMessage(conversationId, content, messageType = 'text', roundInvite) {
    return this.request(`/messages/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, messageType, roundInvite }),
    });
  }

  async markConversationAsRead(conversationId) {
    return this.request(`/messages/conversations/${conversationId}/read`, { method: 'PUT' });
  }

  async getUnreadCount() {
    return this.request('/messages/unread-count');
  }

  // Round endpoints
  async getRounds(latitude, longitude, radius = 50) {
    const params = new URLSearchParams();
    if (latitude && longitude) {
      params.append('latitude', latitude.toString());
      params.append('longitude', longitude.toString());
      params.append('radius', radius.toString());
    }

    return this.request(`/rounds?${params}`);
  }

  async getMyRounds() {
    return this.request('/rounds/mine');
  }

  async getRound(roundId) {
    return this.request(`/rounds/${roundId}`);
  }

  async createRound(roundData) {
    return this.request('/rounds', {
      method: 'POST',
      body: JSON.stringify(roundData),
    });
  }

  async joinRound(roundId) {
    return this.request(`/rounds/${roundId}/join`, { method: 'POST' });
  }

  async confirmParticipant(roundId, participantId) {
    return this.request(`/rounds/${roundId}/participants/${participantId}/confirm`, {
      method: 'PUT',
    });
  }

  async declineParticipant(roundId, participantId) {
    return this.request(`/rounds/${roundId}/participants/${participantId}/decline`, {
      method: 'PUT',
    });
  }

  async cancelRound(roundId) {
    return this.request(`/rounds/${roundId}/cancel`, { method: 'PUT' });
  }

  // Feed endpoints
  async getFeed(limit = 20, before) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (before) params.append('before', before);
    return this.request(`/feed?${params}`);
  }

  async getUserActivity(userId, limit = 10) {
    return this.request(`/feed/user/${userId}?limit=${limit}`);
  }

  async getMutualConnections(userId) {
    return this.request(`/connections/mutual/${userId}`);
  }

  async createPost(postData) {
    return this.request('/feed', { method: 'POST', body: JSON.stringify(postData) });
  }

  async toggleLike(postId) {
    return this.request(`/feed/${postId}/like`, { method: 'POST' });
  }

  async getComments(postId) {
    return this.request(`/feed/${postId}/comments`);
  }

  async addComment(postId, content) {
    return this.request(`/feed/${postId}/comments`, { method: 'POST', body: JSON.stringify({ content }) });
  }

  async deletePost(postId) {
    return this.request(`/feed/${postId}`, { method: 'DELETE' });
  }

  // Notification endpoints
  async getNotifications(limit = 30, before) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (before) params.append('before', before);
    return this.request(`/notifications?${params}`);
  }

  async getNotificationUnreadCount() {
    return this.request('/notifications/unread-count');
  }

  async markNotificationRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, { method: 'PUT' });
  }

  async markAllNotificationsRead() {
    return this.request('/notifications/read-all', { method: 'PUT' });
  }

  // Marketplace endpoints
  async getMarketplace(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') params.append(k, v); });
    return this.request(`/marketplace?${params}`);
  }

  async createTeeTimePost(data) {
    return this.request('/marketplace', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTeeTimeStatus(postId, status) {
    return this.request(`/marketplace/${postId}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
  }

  async deleteTeeTimePost(postId) {
    return this.request(`/marketplace/${postId}`, { method: 'DELETE' });
  }
}

export const api = new ApiService();
export default api;
